/**
 * HorizontalBars — single-series horizontal bar list. Used for the
 * top-devices breakdown.
 *
 * Each row is a label + count + bar; the largest entry sets 100%
 * width and everything else scales relative. The component is a
 * pure flex layout — no SVG — so it inherits container styling
 * automatically.
 */

interface BarRow {
  readonly label: string;
  readonly value: number;
  readonly subtitle?: string;
}

interface Props {
  readonly rows: readonly BarRow[];
  /** Custom value formatter — defaults to toLocaleString. */
  readonly formatValue?: (v: number) => string;
  /** Bar fill colour. Tailwind classes resolve at build time so we accept a raw colour. */
  readonly colour?: string;
}

export function HorizontalBars({
  rows,
  formatValue = (v) => v.toLocaleString(),
  colour = "#1DB954",
}: Props): JSX.Element {
  if (rows.length === 0) {
    return <p className="text-xs text-simuflex-slate">No data in this window.</p>;
  }
  const maxValue = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = Math.round((r.value / maxValue) * 100);
        return (
          <div key={r.label}>
            <div className="flex justify-between items-baseline text-xs">
              <span className="font-semibold text-simuflex-navy">{r.label}</span>
              <span className="text-simuflex-slate">{formatValue(r.value)}</span>
            </div>
            {r.subtitle ? (
              <p className="text-[10px] text-simuflex-slate -mt-0.5">{r.subtitle}</p>
            ) : null}
            <div className="mt-0.5 h-2 w-full bg-simuflex-mist rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${pct}%`, backgroundColor: colour }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
